import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createVNode as _createVNode, createElementVNode as _createElementVNode, toDisplayString as _toDisplayString, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, createTextVNode as _createTextVNode, withCtx as _withCtx, withModifiers as _withModifiers } from "vue"

const _hoisted_1 = { class: "max-w-xl w-screen p-8 flex flex-col" }
const _hoisted_2 = { class: "flex flex-row items-start space-x-4" }
const _hoisted_3 = { class: "flex bg-gray-500 rounded-lg" }
const _hoisted_4 = { class: "text-xl font-semibold text-gray-900 self-center" }
const _hoisted_5 = { class: "mt-8 w-full justify-start flex flex-col space-y-4" }
const _hoisted_6 = {
  key: 0,
  class: "mt-8 w-full justify-start flex flex-col space-y-4"
}
const _hoisted_7 = { class: "mt-8 w-full justify-start flex flex-col space-y-4" }
const _hoisted_8 = { class: "mt-8 flex flex-end justify-end space-x-4" }

import { computed, onBeforeMount, PropType, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { BaseButton } from '@/lib/components/Button';
import { BaseSelect } from '@/lib/components/Select';
import HospitalTeamSelect from '@/lib/components/Programs/HospitalTeamSelect.vue';
import CommunityTeamSelect from '@/lib/components/Programs/CopdProgram/CommunityTeamSelect.vue';
import { BaseIcon } from '@/lib/components/Icon';
import { CopdAssignableOrganisationService, CopdProgramService, FilterTagService } from '@/services/api';
import { IOption, IValidationError } from '@/lib';
import { useNotificationStore } from '@/stores/notification.store';
import { CopdProgramRequest, FilterTag, Organisation, Tag, TagGroupName } from '@/models';
import { useSessionStore } from '@/stores/session.store';
import { AxiosError } from 'axios';


export default /*#__PURE__*/_defineComponent({
  __name: 'AssignToTeamModal',
  props: {
  programId: {
    type: String,
    required: true
  },
  assignedHospitalTeam: {
    type: Object as PropType<Tag | null>,
    default: null
  },
  assignedOrg: {
    type: Object as PropType<Organisation | null>,
    default: null
  },
  assignedCommunityTeam: {
    type: Object as PropType<Tag | null>,
    default: null
  }
},
  emits: ["close", "record-event", "update-assigned-org", "update:assigned-hospital-team", "update:assigned-community-team"],
  setup(__props, { emit }: { emit: ({
  (e: 'close'): void;
  (e: 'record-event', event: string): void;
  (e: 'update-assigned-org', organisation: Organisation | null): void;
  (e: 'update:assigned-hospital-team', team: Tag | null): void;
  (e: 'update:assigned-community-team', team: Tag | null): void
}), expose: any, slots: any, attrs: any }) {

const props = __props;

const { t } = useI18n();



const hospitalTeams = ref<FilterTag[]>([]);
const errors = ref<IValidationError>({
  errors: {},
  message: ''
});
const selectedHospitalTeam = ref<Tag | null>(null);
const fetchErrors = ref({});
const selectedCommunityOrg = ref<Organisation | null>(null);
const selectedCommunityTeam = ref<Tag | null>(null);
const assignableOrganisations = ref<Organisation[]>([]);
const assignableOrganisationService = new CopdAssignableOrganisationService();
const copdProgramService = new CopdProgramService();
const tagService = new FilterTagService();
const sessionStore = useSessionStore();
const notificationStore = useNotificationStore();

const currentOrganisation = computed<Partial<Organisation>>(() => sessionStore.currentOrganisation || {});
const currentOrganisationIsCommunityOrganisation = computed<boolean>(() => assignableOrganisations.value
  .map((organisation: Organisation) => organisation.id)
  .includes(currentOrganisation.value.id)
);
const organisationOptions = computed<Array<IOption>>(() => assignableOrganisations.value.map((organisation) => ({
  value: organisation.id,
  label: organisation.name
})));

onBeforeMount(() => {
  selectedHospitalTeam.value = props.assignedHospitalTeam;
  selectedCommunityOrg.value = props.assignedOrg;
  selectedCommunityTeam.value = props.assignedCommunityTeam;
  fetchAssignableCommunityOrgs();
  fetchHospitalTeams();
  emit('record-event', 'started assigning patient to hospital team / community team / organisation');
});
const updateCommunityOrganisation = (organisationId: string): void => {
  resetValidationErrorMessages();
  selectedCommunityTeam.value = null;
  selectedCommunityOrg.value = assignableOrganisations.value
    .find((organisation) => organisation.id === organisationId) || null;
};
const updateHospitalTeam = (tag: Tag): void => {
  selectedHospitalTeam.value = tag;
  resetValidationErrorMessages();
};
const updateCommunityTeam = (tag: Tag): void => {
  resetValidationErrorMessages();
  selectedCommunityTeam.value = tag;
};
const resetValidationErrorMessages = (): void => {
  errors.value = {
    errors: {},
    message: ''
  };
};
const fetchAssignableCommunityOrgs = async () => {
  try {
    const response = await assignableOrganisationService.index();
    assignableOrganisations.value = response.data;
  } catch (e) {
    await notificationStore.addErrorNotification({
      title: t('custom.uhb.copd.error-fetching-assignable-organisations')
    });
  }
};
const assignTeams = async () => {
  const copdProgramRequest: Partial<CopdProgramRequest> = {
    hospital_team: ''
  };

  if (selectedHospitalTeam.value) {
    copdProgramRequest.hospital_team = selectedHospitalTeam.value.id;
  }

  if (selectedCommunityOrg.value) {
    copdProgramRequest.assigned_organisational_unit_id = selectedCommunityOrg.value.id;
  }

  if (selectedCommunityTeam.value) {
    copdProgramRequest.community_team = selectedCommunityTeam.value.id;
  }

  try {
    await copdProgramService.update(
      props.programId,
      copdProgramRequest
    );

    emit('update-assigned-org', selectedCommunityOrg.value);
    emit('update:assigned-hospital-team', selectedHospitalTeam.value);
    emit('update:assigned-community-team', selectedCommunityTeam.value);

    emit('record-event', 'completed assigning patient to hospital team / community team / organisation');
    emit('close');
  } catch (e) {
    if (e.response?.data) {
      errors.value = e.response.data;
    }
  }
};
const fetchHospitalTeams = async () => {
  try {
    hospitalTeams.value = await tagService.getTagsForGroupName(TagGroupName.HospitalTeam);
  } catch (e) {
    fetchErrors.value = {};
    if (e.response?.status === 422) {
      fetchErrors.value = (e as AxiosError).response.data?.errors;
    }
  }
};

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createElementVNode("div", _hoisted_2, [
      _createElementVNode("div", _hoisted_3, [
        _createVNode(_unref(BaseIcon), {
          name: "crossroad",
          stroke: false,
          height: "2rem"
        })
      ]),
      _createElementVNode("div", _hoisted_4, _toDisplayString(_ctx.$t('custom.uhb.copd.assign-teams')), 1)
    ]),
    _createElementVNode("div", _hoisted_5, [
      _createVNode(HospitalTeamSelect, {
        "hospital-team": selectedHospitalTeam.value?.id,
        "hospital-teams": hospitalTeams.value,
        error: errors.value?.errors?.hospital_team?.[0],
        disabled: _unref(currentOrganisationIsCommunityOrganisation),
        "data-dusk": "assign-to-hospital-team-options",
        "onUpdate:hospitalTeam": updateHospitalTeam
      }, null, 8, ["hospital-team", "hospital-teams", "error", "disabled"])
    ]),
    (_unref(organisationOptions).length)
      ? (_openBlock(), _createElementBlock("div", _hoisted_6, [
          _createVNode(_unref(BaseSelect), {
            "model-value": selectedCommunityOrg.value?.id,
            error: errors.value?.errors?.assigned_organisational_unit_id?.[0],
            label: _ctx.$t('custom.uhb.copd.community-org'),
            options: _unref(organisationOptions),
            placeholder: _ctx.$t('custom.uhb.copd.select-community-org'),
            disabled: !!__props.assignedOrg,
            "data-dusk": "assign-to-organisation-options",
            "onUpdate:modelValue": updateCommunityOrganisation
          }, null, 8, ["model-value", "error", "label", "options", "placeholder", "disabled"])
        ]))
      : _createCommentVNode("", true),
    _createElementVNode("div", _hoisted_7, [
      _createVNode(CommunityTeamSelect, {
        "organisational-unit": selectedCommunityOrg.value?.id,
        "community-team": selectedCommunityTeam.value?.id,
        errors: errors.value?.errors?.community_team?.[0],
        "data-dusk": "assign-to-community-team-options",
        "onUpdate:communityTeam": updateCommunityTeam
      }, null, 8, ["organisational-unit", "community-team", "errors"])
    ]),
    _createElementVNode("div", _hoisted_8, [
      _createVNode(_unref(BaseButton), {
        size: "large",
        color: "ghost",
        onClick: _cache[0] || (_cache[0] = ($event: any) => (_ctx.$emit('close')))
      }, {
        default: _withCtx(() => [
          _createTextVNode(_toDisplayString(_ctx.$t('platform.common.cancel')), 1)
        ]),
        _: 1
      }),
      _createVNode(_unref(BaseButton), {
        size: "large",
        color: "primary",
        "data-dusk": "assign-to-organisation-update",
        onClick: _withModifiers(assignTeams, ["prevent"])
      }, {
        default: _withCtx(() => [
          _createTextVNode(_toDisplayString(_ctx.$t('platform.common.update')), 1)
        ]),
        _: 1
      }, 8, ["onClick"])
    ])
  ]))
}
}

})